import DocumentVariableMapper from "./documentMapper";
import { SubmitRequestDialog } from "./documentMapper/dialogs";
import {
  SkipNextRounded as SkipIcon,
  CloudUploadRounded as UploadIcon,
  DescriptionRounded as DocIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useTheme,
} from "@mui/material";
import Button from "components/common/Button";
import { createFileEntry } from "fileUtils";
import { useCallback, useEffect, useState } from "react";

const BulkTemplateReview = ({
  onSubmitRequest,
  onComplete,
  onCancel,
  // Server-side PDF preview wiring. When supplied, the per-file mapper
  // renders via the LibreOffice convert path (matching New Template +
  // Edit Existing v2), and we additionally prewarm the next file in the
  // queue so opening it feels instant.
  useServerSidePdfPreview = false,
  convertFileToPdfFn = null,
}) => {
  const theme = useTheme();

  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [isUploading, setIsUploading] = useState(false);
  const [completedFiles, setCompletedFiles] = useState([]);

  const isReviewing = currentIndex >= 0;
  const currentFile = uploadedFiles[currentIndex];
  const totalFiles = uploadedFiles.length;
  const isLastFile = currentIndex === totalFiles - 1;

  // While the user is reviewing file N, kick off the server-side PDF
  // conversion for file N+1 in the background. Backend caches by file_id
  // so the mapper open for N+1 just hits the cache when they hit "Next".
  // Fire-and-forget; errors silenced.
  useEffect(() => {
    if (!useServerSidePdfPreview || !convertFileToPdfFn) return;
    const next = uploadedFiles[currentIndex + 1];
    if (
      !next ||
      !next.s3FileId ||
      (next.fileType || "").toLowerCase().includes("pdf")
    ) {
      return;
    }
    convertFileToPdfFn({ fileId: next.s3FileId })
      .unwrap()
      .catch(() => {});
  }, [
    currentIndex,
    uploadedFiles,
    useServerSidePdfPreview,
    convertFileToPdfFn,
  ]);

  const handleFilesSelect = useCallback(async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    setFiles(selectedFiles);
    setIsUploading(true);

    const uploaded = [];
    for (const file of selectedFiles) {
      try {
        const fileId = await createFileEntry(file, false);
        uploaded.push({
          file,
          name: file.name,
          s3FileId: fileId,
          fileType: file.name.endsWith(".pdf") ? "pdf" : "docx",
        });
      } catch {
        uploaded.push({
          file,
          name: file.name,
          s3FileId: null,
          fileType: null,
          error: true,
        });
      }
    }

    setUploadedFiles(uploaded);
    setIsUploading(false);
    setCurrentIndex(0);
    if (e.target?.value != null) e.target.value = null;
  }, []);

  const [submitError, setSubmitError] = useState(null);
  const [noChangesDialogOpen, setNoChangesDialogOpen] = useState(false);

  const advanceToNext = useCallback(() => {
    if (isLastFile) {
      onComplete?.();
    } else {
      setCurrentIndex((prev) => prev + 1);
    }
  }, [isLastFile, onComplete]);

  const handleNoChangesRequired = useCallback(() => {
    if (!currentFile) return;
    setNoChangesDialogOpen(true);
  }, [currentFile]);

  const handleConfirmNoChanges = useCallback(
    async (requestData) => {
      if (!currentFile) return;
      setSubmitError(null);
      setNoChangesDialogOpen(false);

      try {
        await onSubmitRequest({
          ...requestData,
          status: "NO_CHANGES_REQUIRED",
        });

        setCompletedFiles((prev) => [
          ...prev,
          { name: currentFile.name, action: "no_changes" },
        ]);
        advanceToNext();
      } catch (e) {
        setSubmitError(
          `Failed to submit ${currentFile.name}: ${e.message || e}`
        );
      }
    },
    [currentFile, onSubmitRequest, advanceToNext]
  );

  const handleSubmitWithChanges = useCallback(
    async (requestData) => {
      setSubmitError(null);

      try {
        await onSubmitRequest({
          ...requestData,
          status: "SUBMITTED",
        });

        setCompletedFiles((prev) => [
          ...prev,
          { name: currentFile.name, action: "submitted" },
        ]);
        advanceToNext();
      } catch (e) {
        setSubmitError(
          `Failed to submit ${currentFile.name}: ${e.message || e}`
        );
      }
    },
    [currentFile, onSubmitRequest, advanceToNext]
  );

  const [isDragging, setIsDragging] = useState(false);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const droppedFiles = Array.from(e.dataTransfer.files || []).filter((f) =>
        /\.(docx?|pdf)$/i.test(f.name)
      );
      if (droppedFiles.length > 0) {
        handleFilesSelect({ target: { files: droppedFiles } });
      }
    },
    [handleFilesSelect]
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  if (!isReviewing) {
    return (
      <Stack spacing={3} alignItems={"center"} sx={{ py: 4 }}>
        <Paper
          variant={"outlined"}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          sx={{
            p: 6,
            textAlign: "center",
            borderStyle: "dashed",
            borderColor: isDragging
              ? theme.palette.primary.dark
              : theme.palette.primary.main,
            backgroundColor: isDragging
              ? `${theme.palette.primary.main}18`
              : `${theme.palette.primary.main}08`,
            cursor: "pointer",
            width: "100%",
            maxWidth: 600,
            transition: "all 0.15s ease",
            "&:hover": {
              borderColor: theme.palette.primary.dark,
              backgroundColor: `${theme.palette.primary.main}12`,
            },
          }}
          component={"label"}
        >
          <input
            type={"file"}
            accept={".docx,.doc,.pdf"}
            multiple
            onChange={handleFilesSelect}
            hidden
          />
          <UploadIcon
            sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2 }}
          />
          <Typography variant={"h6"} sx={{ fontWeight: 600, mb: 1 }}>
            {"Upload Templates for Review"}
          </Typography>
          <Typography variant={"body2"} color={"text.secondary"}>
            {
              "Drag and drop or click to select multiple .docx or .pdf files. Review each template sequentially."
            }
          </Typography>
        </Paper>

        {isUploading && (
          <Box sx={{ width: "100%", maxWidth: 600 }}>
            <LinearProgress />
            <Typography
              variant={"caption"}
              color={"text.secondary"}
              sx={{ mt: 1 }}
            >
              {`Uploading ${files.length} file${
                files.length !== 1 ? "s" : ""
              }...`}
            </Typography>
          </Box>
        )}
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Paper variant={"outlined"} sx={{ p: 2 }}>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Stack direction={"row"} alignItems={"center"} spacing={2}>
            <DocIcon color={"primary"} />
            <Typography variant={"subtitle1"} sx={{ fontWeight: 600 }}>
              {`Template ${currentIndex + 1} of ${totalFiles}`}
            </Typography>
            <Chip
              label={currentFile?.name}
              size={"small"}
              variant={"outlined"}
            />
          </Stack>
          <Button
            variant={"outlined"}
            color={"success"}
            startIcon={<SkipIcon />}
            onClick={handleNoChangesRequired}
          >
            {"No Changes Required"}
          </Button>
        </Stack>

        <Box sx={{ mt: 2 }}>
          {totalFiles <= 8 ? (
            <Stepper activeStep={currentIndex} alternativeLabel>
              {uploadedFiles.map((uf) => {
                const completed = completedFiles.find(
                  (cf) => cf.name === uf.name
                );
                return (
                  <Step key={uf.name} completed={!!completed}>
                    <StepLabel
                      error={uf.error}
                      optional={
                        completed ? (
                          <Typography variant={"caption"}>
                            {completed.action === "no_changes"
                              ? "No changes"
                              : "Submitted"}
                          </Typography>
                        ) : null
                      }
                    >
                      {uf.name.length > 20
                        ? uf.name.slice(0, 17) + "..."
                        : uf.name}
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          ) : (
            <Box>
              <LinearProgress
                variant={"determinate"}
                value={((currentIndex + 1) / totalFiles) * 100}
                sx={{ height: 8, borderRadius: 4, mb: 1 }}
              />
              <Typography variant={"caption"} color={"text.secondary"}>
                {`${completedFiles.length} of ${totalFiles} completed`}
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {submitError && (
        <Alert severity={"error"} onClose={() => setSubmitError(null)}>
          {submitError}
        </Alert>
      )}

      {currentFile?.error ? (
        <Alert severity={"error"}>
          {`Failed to upload ${currentFile.name}. `}
          <Button
            size={"small"}
            onClick={() => {
              setCompletedFiles((prev) => [
                ...prev,
                { name: currentFile.name, action: "error" },
              ]);
              if (isLastFile) {
                onComplete?.();
              } else {
                setCurrentIndex((prev) => prev + 1);
              }
            }}
          >
            {"Skip"}
          </Button>
        </Alert>
      ) : (
        <DocumentVariableMapper
          requestType={"EDIT_TEMPLATE"}
          onSubmitRequest={handleSubmitWithChanges}
          onCancel={onCancel}
          initialFile={currentFile}
          submitLabel={isLastFile ? "Submit & Finish" : "Submit & Next"}
          useServerSidePdfPreview={useServerSidePdfPreview}
          convertFileToPdfFn={convertFileToPdfFn}
        />
      )}

      <SubmitRequestDialog
        open={noChangesDialogOpen}
        onClose={() => setNoChangesDialogOpen(false)}
        onSubmit={handleConfirmNoChanges}
        fileName={currentFile?.name || ""}
        file={currentFile?.file}
        s3FileId={currentFile?.s3FileId}
        placements={[]}
        customFields={[]}
        requestType={"EDIT_TEMPLATE"}
        annotations={[]}
        allowEmpty
      />
    </Stack>
  );
};

export default BulkTemplateReview;
